import { num, money } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

function DRow({ k, v, mono = true }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-1.5">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className={`text-right text-xs ${mono ? "metric-num" : ""} font-medium`}>{v}</span>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="space-y-1">
      <div className="label-caps pt-2">{title}</div>
      {children}
    </div>
  );
}

export default function DiagnosticsDrawer({ open, onOpenChange, result, selectedRate }) {
  const d = result?.diagnostics;
  const s = result?.stack_result;
  const isMulti = !!(d && (d.line_item_count != null || !d.product_values));
  const lineItems = (s && s.line_items) || result?.line_items || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="diagnostics-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">Calculation Diagnostics</SheetTitle>
          <SheetDescription>Exactly how the freight result was calculated.</SheetDescription>
        </SheetHeader>
        {!d ? (
          <p className="mt-6 text-sm text-muted-foreground">Calculate a quote to see how the result was produced.</p>
        ) : (
          <div className="mt-4 space-y-3 pb-10">
            <div className={`rounded-md border px-3 py-2 text-sm font-semibold ${d.trust_recommendation === "trusted" ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"}`}>
              {d.trust_recommendation === "trusted" ? "Rate looks trustworthy" : "Review recommended before quoting"}
            </div>

            {isMulti ? (
              <>
                <Group title="Shipment Summary">
                  <DRow k="Line items" v={num(d.line_item_count)} />
                  <DRow k="Total weight" v={`${num(d.total_weight)} lb`} />
                  <DRow k="Total handling units" v={num(d.total_handling_units)} />
                  <DRow k="Total linear feet" v={`${num(d.total_linear_feet, 1)} ft`} />
                  <DRow k="Calculated NMFC class" v={d.calculated_class || "—"} />
                  <DRow k="Rating class used" v={d.rating_class || "—"} />
                  <DRow k="FAK applied" v={d.fak_applied ? "Yes" : "No"} />
                </Group>

                {lineItems.length > 0 && (
                  <Group title="Line Items">
                    {lineItems.map((li, i) => (
                      <DRow
                        key={i}
                        k={`${i + 1}. ${li.description || li.product_name || "Item"}`}
                        v={`${num(li.units)}u · ${num(li.weight)} lb · Cl ${li.freight_class || "—"}${li.length ? ` · ${num(li.length)}×${num(li.width)}×${num(li.height)} in` : ""}`}
                        mono={false}
                      />
                    ))}
                  </Group>
                )}

                <Group title="Carrier Request Preview">
                  <DRow k="Rating class" v={d.rating_class || "—"} />
                  <DRow k="Total weight" v={`${num(d.total_weight)} lb`} />
                  <DRow k="Handling units" v={num(d.total_handling_units)} />
                  <DRow k="Rate source" v={d.rate_source || "—"} mono={false} />
                  <DRow k="FedEx account" v={d.fedex_account_used} mono={false} />
                </Group>
              </>
            ) : (
              <>
                <Group title="Product Values">
                  <DRow k="Dimensions (L×W×H)" v={`${d.product_values.length} × ${d.product_values.width} × ${d.product_values.height} in`} />
                  <DRow k="Weight" v={`${d.product_values.weight} lb`} />
                  <DRow k="Nesting height" v={`${d.product_values.nesting_height} in`} />
                  <DRow k="Height limit" v={`${num(d.height_limit)} in`} />
                </Group>

                <Group title="Stack Calculation">
                  <DRow k="Catalog stack limit" v={num(d.catalog_stack_limit)} />
                  <DRow k="Height pieces limit" v={num(d.height_pieces_limit)} />
                  <DRow k="Enforced stack limit" v={num(d.enforced_stack_limit)} />
                  <DRow k="Full stack count" v={num(d.full_stack_count)} />
                  <DRow k="Partial stack pieces" v={num(d.last_stack_pieces)} />
                  <DRow k="Total handling units" v={num(d.total_handling_units)} />
                  <DRow k="Total shipment weight" v={`${num(d.total_shipment_weight)} lb`} />
                  <DRow k="Tall stack height" v={`${num(d.tall_stack_height, 1)} in`} />
                  <DRow k="Last stack height" v={d.last_stack_height ? `${num(d.last_stack_height, 1)} in` : "—"} />
                </Group>

                <Group title="Density & Class">
                  <DRow k="Calculated density" v={num(d.calculated_density, 3)} />
                  <DRow k="Full stack density" v={num(d.full_stack_density, 2)} />
                  <DRow k="Last stack density" v={d.last_stack_density ? num(d.last_stack_density, 2) : "—"} />
                  <DRow k="Calculated NMFC class" v={d.calculated_class || "—"} />
                  <DRow k="FedEx FAK class used" v={d.fedex_fak_class || "—"} />
                  <DRow k="FAK applied" v={d.fak_applied ? "Yes" : "No"} />
                  <DRow k="FAK reason" v={d.fak_reason} mono={false} />
                </Group>

                <Group title="Carrier Request Preview">
                  <DRow k="Origin ZIP" v={d.carrier_request_preview.origin_zip || "—"} />
                  <DRow k="Destination ZIP" v={d.carrier_request_preview.destination_zip || "—"} />
                  <DRow k="Handling units" v={num(d.carrier_request_preview.handling_units)} />
                  <DRow k="Total weight" v={`${num(d.carrier_request_preview.total_weight)} lb`} />
                  <DRow k="Rating class" v={d.carrier_request_preview.rating_class || "—"} />
                  <DRow k="Service type" v={d.service_type_requested} mono={false} />
                  <DRow k="FedEx account" v={d.fedex_account_used} mono={false} />
                  <DRow k="Accessorials sent" v={(d.accessorials_sent || []).join(", ") || "none"} mono={false} />
                </Group>
              </>
            )}

            {selectedRate && (
              <Group title="Carrier Response Summary">
                <DRow k="Carrier" v={selectedRate.carrier_name} mono={false} />
                <DRow k="Rate source" v={selectedRate.rate_source} mono={false} />
                <DRow k="Total cost" v={money(selectedRate.total_carrier_cost)} />
                <DRow k="Transit days" v={selectedRate.transit_days ?? "—"} />
                <DRow k="Quote ID" v={selectedRate.quote_id || "—"} />
                <DRow k="Confidence" v={`${selectedRate.confidence_score} (${selectedRate.confidence_level})`} />
                {(selectedRate.warnings || []).length > 0 && (
                  <DRow k="Warnings" v={selectedRate.warnings.join(" ")} mono={false} />
                )}
              </Group>
            )}

            {(result.warnings || []).length > 0 && (
              <Group title="Warnings & Exceptions">
                {result.warnings.map((w, i) => (
                  <div key={i} className={`rounded-md border px-2.5 py-1.5 text-xs ${w.level === "error" ? "border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))]" : w.level === "warning" ? "border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]" : "border-border text-muted-foreground"}`}>
                    {w.message}
                  </div>
                ))}
              </Group>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
